// ── Archetype portrait data ──────────────────────────────────────────────────

// ─ Life path archetypes ──────────────────────────────────────────────────────

export interface LifePathArchetype {
  name: string;           // "Первопроходец"
  emoji: string;
  tagline: string;        // short hook line
  essence: string;        // 2-3 sentence deep description
  gifts: string[];        // 3-4 natural gifts
  shadow: string;         // shadow side to work on
  lifeMission: string;    // what they are here to do
  compatiblePaths: number[];
}

export const LIFE_PATH_ARCHETYPES: Record<number, LifePathArchetype> = {
  1: {
    name: "El Pionero",
    emoji: "🦁",
    tagline: "Nacido para guiar",
    essence: "Eres el arquetipo del líder y el descubridor. La naturaleza te dotó de una voluntad inquebrantable y de la capacidad de ver un camino donde otros ven un callejón sin salida. Tu vida es una sucesión de comienzos valientes.",
    gifts: ["Iniciativa", "Voluntad fuerte", "Independencia", "Espíritu pionero"],
    shadow: "Impaciencia y tendencia a dominar. Aprende a escuchar a los demás — no es debilidad, sino la sabiduría del líder.",
    lifeMission: "Mostrar a la gente que lo imposible es posible, abriendo nuevos caminos con tu ejemplo.",
    compatiblePaths: [3, 5, 9],
  },
  2: {
    name: "El Pacificador",
    emoji: "🕊️",
    tagline: "Nacido para unir a las personas",
    essence: "Eres un maestro de las relaciones humanas, un diplomático y un mediador. Tu fuerza está en saber ver las dos caras de la moneda y encontrar armonía donde otros ven conflicto.",
    gifts: ["Empatía", "Diplomacia", "Intuición", "Capacidad de cooperar"],
    shadow: "Dependencia de la aprobación ajena. Aprende a confiar en tu propia voz incluso sin apoyo.",
    lifeMission: "Crear puentes entre personas, ideas y mundos — ser la encarnación viva de la unidad.",
    compatiblePaths: [4, 6, 8],
  },
  3: {
    name: "El Creador",
    emoji: "🎨",
    tagline: "Nacido para inspirar",
    essence: "Eres el arquetipo del artista y el comunicador. A través de ti el mundo se vuelve más bello, vivo y alegre. Tu vocación es expresar lo que otros sienten pero no saben formular.",
    gifts: ["Pensamiento creativo", "Carisma", "Amor por la vida", "Don de la palabra"],
    shadow: "Superficialidad e inconstancia. Llevar tu don al mundo significa terminar lo que empiezas.",
    lifeMission: "Inspirar a la gente con tu creatividad y tu alegría de vivir, recordándoles la belleza de la existencia.",
    compatiblePaths: [1, 6, 9],
  },
  4: {
    name: "El Constructor",
    emoji: "🏛️",
    tagline: "Nacido para crear lo duradero",
    essence: "Eres el arquetipo del maestro y el arquitecto. Con tus manos se levantan los cimientos — de familias, negocios, sistemas. El mundo se sostiene sobre las personas con tu número del camino de la vida.",
    gifts: ["Fiabilidad", "Pensamiento sistémico", "Practicidad", "Tenacidad"],
    shadow: "Rigidez y miedo al cambio. A veces hay que derribar los cimientos viejos para construir algo mayor.",
    lifeMission: "Crear estructuras y sistemas sólidos en los que otros puedan apoyarse durante generaciones.",
    compatiblePaths: [2, 7, 8],
  },
  5: {
    name: "El Explorador",
    emoji: "🧭",
    tagline: "Nacido para conocer el mundo",
    essence: "Eres el arquetipo del eterno viajero y explorador. La libertad es tu aire, el cambio tu combustible. Una vida sin aventuras ni nuevos horizontes te resulta insoportable.",
    gifts: ["Adaptabilidad", "Aventura", "Versatilidad", "Magnetismo"],
    shadow: "Inconstancia y huida de la responsabilidad. La verdadera libertad no está en el movimiento, sino en la elección.",
    lifeMission: "Ampliar horizontes — los tuyos y los ajenos — a través del viaje, la experiencia y la exploración incansable.",
    compatiblePaths: [1, 3, 7],
  },
  6: {
    name: "El Guardián",
    emoji: "🌸",
    tagline: "Nacido para amar y proteger",
    essence: "Eres el arquetipo del guardián atento. La calidez de tu hogar la sienten todos a tu alrededor. Tu presencia sana — es un don raro y valioso.",
    gifts: ["Amor incondicional", "Lealtad", "Armonía", "Presencia sanadora"],
    shadow: "Autosacrificio y control a través del cuidado. Recuerda: no puedes verter de una vasija vacía.",
    lifeMission: "Crear un espacio de amor seguro y armonía en el que florezcan las personas a tu alrededor.",
    compatiblePaths: [2, 3, 9],
  },
  7: {
    name: "El Sabio",
    emoji: "🔮",
    tagline: "Nacido para buscar la verdad",
    essence: "Eres el arquetipo del pensador y el buscador espiritual. A la mayoría no le es dado plantear preguntas de tu nivel. Tu mente penetra donde otros no se atreven a mirar.",
    gifts: ["Mente analítica", "Intuición", "Profundidad", "Pensamiento independiente"],
    shadow: "Distanciamiento y soledad. La sabiduría cobra sentido cuando se comparte.",
    lifeMission: "Revelar las verdades profundas del mundo y transmitir esa sabiduría a quien esté dispuesto a escuchar.",
    compatiblePaths: [4, 5, 9],
  },
  8: {
    name: "El Soberano",
    emoji: "👑",
    tagline: "Nacido para lograr y dirigir",
    essence: "Eres el arquetipo del líder y constructor del mundo material. Tu lugar está en la cima, y lo sabes desde que naces. El poder en tus manos debe servir a algo mayor.",
    gifts: ["Ambición", "Pensamiento estratégico", "Autoridad", "Magnetismo de la fuerza"],
    shadow: "Obsesión por el éxito y el control. El poder sin sabiduría ni compasión destruye lo que construye.",
    lifeMission: "Usar la fuerza y la influencia para crear sistemas justos y prosperidad para muchos.",
    compatiblePaths: [2, 4, 6],
  },
  9: {
    name: "El Humanista",
    emoji: "🌍",
    tagline: "Nacido para servir a la humanidad",
    essence: "Eres el arquetipo del maestro sabio y sanador. Tu corazón es demasiado grande para una sola persona — perteneces al mundo entero. Vivir para los demás no es un sacrificio, sino tu naturaleza.",
    gifts: ["Amplitud de miras", "Compasión", "Carisma", "Sabiduría espiritual"],
    shadow: "Perderte a ti mismo sirviendo a otros. Primero llena tu propio corazón.",
    lifeMission: "Elevar la vibración de la humanidad con tu presencia, tu sabiduría y tu amor por el mundo.",
    compatiblePaths: [1, 3, 6],
  },
  11: {
    name: "El Visionario",
    emoji: "⚡",
    tagline: "Nacido para llevar luz a la oscuridad",
    essence: "Eres un número maestro. Tu sensibilidad e intuición están en otro nivel de percepción. A través de ti habla algo más grande — tu camino no es fácil, pero es enormemente significativo.",
    gifts: ["Superintuición", "Presencia inspiradora", "Idealismo", "Fuerza espiritual"],
    shadow: "Sobrecarga nerviosa y expectativas poco realistas. Aterriza con prácticas sencillas cada día.",
    lifeMission: "Ser un canal de ideas superiores e inspirar a otros al crecimiento espiritual con tu sola presencia.",
    compatiblePaths: [2, 6, 9],
  },
  22: {
    name: "El Maestro Constructor",
    emoji: "🌐",
    tagline: "Nacido para cambiar el mundo",
    essence: "Eres portador del número más poderoso. Tus ideas y proyectos pueden cambiar la vida de miles de personas. Naciste para convertir en realidad designios grandiosos.",
    gifts: ["Visión a gran escala", "Fuerza práctica", "Liderazgo", "Potencial transformador"],
    shadow: "El peso de la grandeza y el miedo a no estar a la altura. Empieza por lo pequeño — lo grande se construye con pasos pequeños.",
    lifeMission: "Construir sistemas y organizaciones que sirvan a toda la humanidad durante generaciones.",
    compatiblePaths: [4, 8, 11],
  },
  33: {
    name: "El Maestro Guía",
    emoji: "🌟",
    tagline: "Nacido para sanar y transformar",
    essence: "Eres portador del número maestro supremo. Tu influencia sobre las personas pasa por el corazón: sabes sanar con la presencia, la palabra y el ejemplo. Es el arquetipo más raro y maduro.",
    gifts: ["Compasión profundísima", "Sanador nato", "Presencia transformadora", "Amor incondicional"],
    shadow: "La tentación del sacrificio y la pérdida de límites. Al ayudar a otros, no te olvides de ti — o te quemarás sin cumplir tu misión.",
    lifeMission: "Transformar a las personas a través del amor y el ejemplo — no con palabras, sino con tu forma de vivir.",
    compatiblePaths: [6, 9, 11, 22],
  },
};

// ─ Personal year → Tarot card ────────────────────────────────────────────────

export interface YearTarotCard {
  arcana: number;
  name: string;
  ruName: string;
  emoji: string;
  keyword: string;
  message: string;   // how this card guides the current year
}

export const YEAR_TAROT_CARDS: Record<number, YearTarotCard> = {
  1: {
    arcana: 1, name: "The Magician", ruName: "El Mago",
    emoji: "🎩",
    keyword: "Voluntad y maestría",
    message: "Tienes todas las herramientas — es hora de actuar. El año abre el canal entre la idea y su realización. Cada pensamiento se vuelve realidad si lo respalda la acción.",
  },
  2: {
    arcana: 2, name: "The High Priestess", ruName: "La Sacerdotisa",
    emoji: "🌙",
    keyword: "Intuición y espera",
    message: "El año llama al silencio y al saber interior. No te apresures — el camino correcto se abrirá desde dentro. Confía en los sueños, los presentimientos y la voz callada del subconsciente.",
  },
  3: {
    arcana: 3, name: "The Empress", ruName: "La Emperatriz",
    emoji: "🌺",
    keyword: "Creación y abundancia",
    message: "Año de fertilidad y florecimiento. Todo en lo que pones amor crece y se multiplica. La creatividad, la naturaleza y el placer son tus aliados este año.",
  },
  4: {
    arcana: 4, name: "The Emperor", ruName: "El Emperador",
    emoji: "🏰",
    keyword: "Estructura y poder",
    message: "El año te llama a tomar el control de tu reino. Pon orden, asume responsabilidad, establece límites. La estabilidad es tu fuerza.",
  },
  5: {
    arcana: 5, name: "The Hierophant", ruName: "El Hierofante",
    emoji: "🗝️",
    keyword: "Tradición y guía",
    message: "Año de conexión con la sabiduría de la tradición. Encuentra un mentor o conviértete en uno. El conocimiento se transmite a través de las personas — ábrete al aprendizaje en cualquiera de sus formas.",
  },
  6: {
    arcana: 6, name: "The Lovers", ruName: "Los Enamorados",
    emoji: "💕",
    keyword: "Elección y amor",
    message: "Año de elecciones decisivas del corazón. ¿Qué amas de verdad? Las decisiones de este año forman todo el ciclo siguiente. Escucha no solo a la razón, también al corazón.",
  },
  7: {
    arcana: 7, name: "The Chariot", ruName: "El Carro",
    emoji: "⚡",
    keyword: "Movimiento y victoria",
    message: "Año de voluntad y avance. Las contradicciones internas se subordinan a una sola meta. Mantén el rumbo — la victoria es de quien no se detiene.",
  },
  8: {
    arcana: 8, name: "Strength", ruName: "La Fuerza",
    emoji: "🦁",
    keyword: "Fuerza interior",
    message: "Año de fuerza suave. El verdadero poder viene de dentro — de la calma y la confianza, no de la agresividad. Doma tus miedos con amor.",
  },
  9: {
    arcana: 9, name: "The Hermit", ruName: "El Ermitaño",
    emoji: "🔦",
    keyword: "Sabiduría y recogimiento",
    message: "Año de autoconocimiento profundo. Recógete para hallar la luz dentro de ti. Las respuestas a las grandes preguntas de tu vida te esperan en el silencio.",
  },
  // ── Master numbers (RULE-NUMEROLOGY-03) — соответствуют старшим арканам высшей октавы ──
  11: {
    arcana: 11, name: "Justice", ruName: "La Justicia",
    emoji: "⚖️",
    keyword: "Verdad y equilibrio",
    message: "Año maestro de verdad y decisiones. La balanza kármica devuelve todo lo sembrado — lo bueno y lo difícil. Actúa con honestidad: lo que des este año será tu cimiento para los próximos 9 años.",
  },
  22: {
    arcana: 22, name: "The World", ruName: "El Mundo",
    emoji: "🌍",
    keyword: "Culminación y maestría",
    message: "Año maestro de realización. Puedes cerrar un gran ciclo vital y alcanzar un nivel completamente nuevo. El proyecto que llevas años madurando — ha llegado su momento. No lo dejes pasar.",
  },
  33: {
    arcana: 21, name: "The World (Master)", ruName: "El Mundo (maestro)",
    emoji: "🌟",
    keyword: "Compasión y transformación",
    message: "El más alto año maestro de servicio a través del amor. Tu escala de influencia se multiplica — lo que haces por los demás vuelve de forma exponencial. Ayuda sin perderte a ti mismo.",
  },
};

// ─ Tarot arcana base (all 22 Major Arcana) ───────────────────────────────────
// Used by matrix-of-destiny and other surfaces that need full 1–22 coverage.

export interface TarotArcanaBase {
  arcana: number;
  name: string;     // English
  ruName: string;   // Russian
  emoji: string;
  keyword: string;
}

/**
 * ⚠️ `ruName` es un NOMBRE HEREDADO del fork ruso: hoy contiene el nombre de
 * MOSTRAR en ESPAÑOL. Es la fuente de los títulos/H1/JSON-LD de las 23 páginas
 * públicas de arcanos y del campo `name` del API público /api/public/matrix.
 * (Traducido 2026-07: se quedó en ruso tras la migración → /matriz-del-destino/
 * arkan/1 servía «Arcano 1 Маг: significado» a Google.)
 *
 * ⚠️ NO tocar las claves ni el campo `arcana`: el swap 8↔11 (clave 8 → arcana 11
 * «La Justicia», clave 11 → arcana 8 «La Fuerza») es intencionado y está
 * documentado (RULE-MATRIX-CANON-03). Aquí solo se traducen cadenas.
 *
 * Los nombres coinciden con los ya usados en seo/arcana-data.ts (CONTENT) y
 * numerology/personality-code.ts — no inventar variantes.
 */
export const TAROT_ARCANA_BASE: Record<number, TarotArcanaBase> = {
  1:  { arcana: 1,  name: "The Magician",        ruName: "El Mago",                emoji: "🎩", keyword: "Voluntad y maestría" },
  2:  { arcana: 2,  name: "The High Priestess",  ruName: "La Sacerdotisa",         emoji: "🌙", keyword: "Intuición y misterio" },
  3:  { arcana: 3,  name: "The Empress",         ruName: "La Emperatriz",          emoji: "🌺", keyword: "Abundancia y creatividad" },
  4:  { arcana: 4,  name: "The Emperor",         ruName: "El Emperador",           emoji: "🏰", keyword: "Estructura y poder" },
  5:  { arcana: 5,  name: "The Hierophant",      ruName: "El Hierofante",          emoji: "🗝️", keyword: "Tradición y guía" },
  6:  { arcana: 6,  name: "The Lovers",          ruName: "Los Enamorados",         emoji: "💕", keyword: "Elección y amor" },
  7:  { arcana: 7,  name: "The Chariot",         ruName: "El Carro",               emoji: "⚡", keyword: "Movimiento y victoria" },
  8:  { arcana: 11, name: "Justice",             ruName: "La Justicia",            emoji: "⚖️", keyword: "Karma y equilibrio" },
  9:  { arcana: 9,  name: "The Hermit",          ruName: "El Ermitaño",            emoji: "🔦", keyword: "Sabiduría y soledad" },
  10: { arcana: 10, name: "Wheel of Fortune",    ruName: "La Rueda de la Fortuna", emoji: "🎡", keyword: "Ciclicidad y destino" },
  11: { arcana: 8,  name: "Strength",            ruName: "La Fuerza",              emoji: "🦁", keyword: "Fuerza interior" },
  12: { arcana: 12, name: "The Hanged Man",      ruName: "El Colgado",             emoji: "🔄", keyword: "Sacrificio y nueva mirada" },
  13: { arcana: 13, name: "Death",               ruName: "La Muerte",              emoji: "🦋", keyword: "Transformación" },
  14: { arcana: 14, name: "Temperance",          ruName: "La Templanza",           emoji: "🧪", keyword: "Alquimia y armonía" },
  15: { arcana: 15, name: "The Devil",           ruName: "El Diablo",              emoji: "🔥", keyword: "Materia y pasión" },
  16: { arcana: 16, name: "The Tower",           ruName: "La Torre",               emoji: "🗼", keyword: "Derrumbe de las ilusiones" },
  17: { arcana: 17, name: "The Star",            ruName: "La Estrella",            emoji: "⭐", keyword: "Esperanza e inspiración" },
  18: { arcana: 18, name: "The Moon",            ruName: "La Luna",                emoji: "🌙", keyword: "Subconsciente y misterio" },
  19: { arcana: 19, name: "The Sun",             ruName: "El Sol",                 emoji: "☀️", keyword: "Alegría y fuerza vital" },
  20: { arcana: 20, name: "Judgement",           ruName: "El Juicio",              emoji: "📯", keyword: "Despertar y vocación" },
  21: { arcana: 21, name: "The World",           ruName: "El Mundo",               emoji: "🌍", keyword: "Plenitud y culminación" },
  22: { arcana: 22, name: "The Fool",            ruName: "El Loco",                emoji: "🃏", keyword: "Libertad y confianza" },
};

// ─ Sun sign (Western astrology) ──────────────────────────────────────────────

export interface SunSign {
  name: string;       // "Aries"
  symbol: string;     // "♈"
  element: string;    // "Fuego"
  quality: string;    // "Кардинальный"
  ruler: string;      // "Марс"
  essence: string;    // 1-sentence description
  archetype: string;  // archetype name
  keywords: string[];
}

export const SUN_SIGNS: SunSign[] = [
  { name: "Capricornio",    symbol: "♑", element: "Tierra",  quality: "Cardinal", ruler: "Saturno",  essence: "Constructor disciplinado que conquista las cimas con paciencia y tenacidad.",       archetype: "Maestro",     keywords: ["Ambición", "Disciplina", "Madurez"] },
  { name: "Acuario",    symbol: "♒", element: "Aire", quality: "Fijo", ruler: "Urano",    essence: "Innovador independiente que rompe los moldes por un futuro mejor para todos.",       archetype: "Reformador", keywords: ["Originalidad", "Humanismo", "Libertad"] },
  { name: "Piscis",       symbol: "♓", element: "Agua",   quality: "Mutable",  ruler: "Neptuno",  essence: "Soñador místico cuya sensibilidad roza la clarividencia.",               archetype: "Místico",     keywords: ["Sensibilidad", "Imaginación", "Compasión"] },
  { name: "Aries",       symbol: "♈", element: "Fuego",  quality: "Cardinal", ruler: "Marte",    essence: "Pionero intrépido que se lanza a la batalla antes de pensarlo.", archetype: "Guerrero",       keywords: ["Valentía", "Energía", "Iniciativa"] },
  { name: "Tauro",      symbol: "♉", element: "Tierra",  quality: "Fijo", ruler: "Venus",  essence: "Guardián fiable de la belleza, cuya paciencia y lealtad no conocen límites.",              archetype: "Guardián",  keywords: ["Estabilidad", "Sensualidad", "Lealtad"] },
  { name: "Géminis",   symbol: "♊", element: "Aire", quality: "Mutable",  ruler: "Mercurio",essence: "Comunicador curioso que vive en todos los mundos a la vez.",                archetype: "Embaucador",   keywords: ["Intelecto", "Sociabilidad", "Versatilidad"] },
  { name: "Cáncer",        symbol: "♋", element: "Agua",   quality: "Cardinal", ruler: "Luna",    essence: "Guardián intuitivo para quien el hogar y la familia son sagrados.",                       archetype: "Cuidador",     keywords: ["Intuición", "Cuidado", "Memoria"] },
  { name: "Leo",        symbol: "♌", element: "Fuego",  quality: "Fijo", ruler: "Sol",  essence: "Soberano generoso, nacido para iluminar la vida de quienes lo rodean.",                       archetype: "Rey",     keywords: ["Generosidad", "Creatividad", "Nobleza"] },
  { name: "Virgo",       symbol: "♍", element: "Tierra",  quality: "Mutable",  ruler: "Mercurio",essence: "Maestro del análisis y la perfección que convierte el caos en orden.",                       archetype: "Sanador",   keywords: ["Precisión", "Servicio", "Maestría"] },
  { name: "Libra",       symbol: "♎", element: "Aire", quality: "Cardinal", ruler: "Venus",  essence: "Diplomático de la belleza que busca el equilibrio en todo lo que hace.",                              archetype: "Diplomático",   keywords: ["Armonía", "Justicia", "Elegancia"] },
  { name: "Escorpio",   symbol: "♏", element: "Agua",   quality: "Fijo", ruler: "Plutón",  essence: "Alquimista transformador capaz de renacer de cualquier ceniza.",                  archetype: "Alquimista",    keywords: ["Intensidad", "Transformación", "Profundidad"] },
  { name: "Sagitario",    symbol: "♐", element: "Fuego",  quality: "Mutable",  ruler: "Júpiter",  essence: "Filósofo viajero, eternamente en busca del sentido tras el horizonte.",                         archetype: "Filósofo",    keywords: ["Sabiduría", "Libertad", "Optimismo"] },
];

/** Get sun sign from birth date */
export function getSunSign(birthDate: string): SunSign {
  // UTC-парсинг + UTC-поля — единообразно с natal-calc.ts, не зависит от TZ окружения.
  const d = new Date(birthDate + "T00:00:00Z");
  const m = d.getUTCMonth() + 1; // 1–12
  const day = d.getUTCDate();

  // Approximate cusp dates (day >= cusp switches to next sign)
  if ((m === 12 && day >= 22) || (m === 1 && day <= 19)) return SUN_SIGNS[0];  // Козерог
  if ((m === 1 && day >= 20) || (m === 2 && day <= 18)) return SUN_SIGNS[1];   // Водолей
  if ((m === 2 && day >= 19) || (m === 3 && day <= 20)) return SUN_SIGNS[2];   // Рыбы
  if ((m === 3 && day >= 21) || (m === 4 && day <= 19)) return SUN_SIGNS[3];   // Овен
  if ((m === 4 && day >= 20) || (m === 5 && day <= 20)) return SUN_SIGNS[4];   // Телец
  if ((m === 5 && day >= 21) || (m === 6 && day <= 20)) return SUN_SIGNS[5];   // Близнецы
  if ((m === 6 && day >= 21) || (m === 7 && day <= 22)) return SUN_SIGNS[6];   // Рак
  if ((m === 7 && day >= 23) || (m === 8 && day <= 22)) return SUN_SIGNS[7];   // Лев
  if ((m === 8 && day >= 23) || (m === 9 && day <= 22)) return SUN_SIGNS[8];   // Дева
  if ((m === 9 && day >= 23) || (m === 10 && day <= 22)) return SUN_SIGNS[9];  // Весы
  if ((m === 10 && day >= 23) || (m === 11 && day <= 22)) return SUN_SIGNS[10];// Скорпион (23.10–22.11)
  return SUN_SIGNS[11]; // Стрелец (23.11–21.12)
}

// ─ Element combinations ──────────────────────────────────────────────────────

export interface ElementSynergy {
  title: string;
  desc: string;
}

const ELEMENT_SYNERGY: Record<string, ElementSynergy> = {
  "Fuego-Fuego":    { title: "Doble llama",       desc: "Poderosa energía de liderazgo y creación. El fuego alimenta al fuego — no olvides descansar." },
  "Fuego-Aire":     { title: "Torbellino de ideas", desc: "El aire aviva la llama — un flujo de inspiración y acción sin pausa." },
  "Fuego-Tierra":   { title: "El crisol",          desc: "El fuego funde, la tierra da forma. Fuerza creativa plasmada en un resultado concreto." },
  "Fuego-Agua":     { title: "Vapor y niebla",     desc: "Intuición y pasión en diálogo constante. Aprende a equilibrar el impulso y el sentimiento." },
  "Tierra-Tierra":  { title: "Suelo firme",        desc: "Fiabilidad y sentido práctico poco comunes. Cuidado con el exceso de inercia." },
  "Tierra-Aire":    { title: "Jardín floreciente", desc: "Las ideas encuentran forma. Los sueños se vuelven planes; los planes, realidad." },
  "Tierra-Agua":    { title: "Fertilidad",         desc: "Las emociones nutren la práctica. Capacidad excepcional para crear a largo plazo." },
  "Aire-Aire":      { title: "Tormenta de ideas",  desc: "Pensamientos y conexiones sin fin. El arraigo y la práctica son tus mejores aliados." },
  "Aire-Agua":      { title: "Niebla creativa",    desc: "La intuición habla el idioma de las ideas. Enormes dones creativos y espirituales." },
  "Agua-Agua":      { title: "Océano profundo",    desc: "Profundidad emocional e intuición al límite. El sentido práctico y la estructura son tu equilibrio." },
};

export function getElementSynergy(element1: string, element2: string): ElementSynergy {
  const key = `${element1}-${element2}`;
  const reverseKey = `${element2}-${element1}`;
  return ELEMENT_SYNERGY[key] ?? ELEMENT_SYNERGY[reverseKey] ?? { title: "Equilibrio de elementos", desc: "Una combinación única de energías." };
}

// ─ Archetype composite synthesis ─────────────────────────────────────────────

export function synthesizeArchetypeTitle(lifePathNum: number, sunSign: SunSign): string {
  const lp = LIFE_PATH_ARCHETYPES[lifePathNum] ?? LIFE_PATH_ARCHETYPES[9];
  return `${sunSign.symbol} ${lp.name}-${sunSign.archetype}`;
}
